'use client';

import * as Styled from './BadgeDropdown.styled';

import { Dropdown, Skeleton, Tooltip, type MenuProps } from 'antd';
import React, { ReactNode } from 'react';

/**
 * Represents a single menu item in the dropdown
 * Used for system roles, permissions, or other categorized lists
 */
export interface DropdownMenuItem {
  /** Display text for the menu item (e.g., "Admin", "Developer") */
  label: ReactNode;

  /** Unique identifier for the item - used for tracking selections */
  key: string;

  /** Optional descriptive text shown below the label (e.g., "Full administrative access") */
  description?: string;

  /** Optional icon or indicator displayed before the label (e.g., "✓", "✗", or Ant Design icon) */
  icon?: ReactNode;

  /** Color of the icon (e.g., "green" for checkmark, "red" for X). Primarily for string-based icons */
  iconColor?: string;
}

/**
 * Represents a group of related menu items with a header
 * Groups organize menu items into categories (e.g., "Administrative Roles", "Standard Roles")
 */
export interface DropdownMenuGroup {
  /** Group header text displayed above the items */
  label: ReactNode;

  /** Array of menu items within this group */
  children: DropdownMenuItem[];

  /** Optional flag to render this group as a footer section with different styling */
  isFooter?: boolean;
}

type DropdownMenuItems = DropdownMenuGroup[];
const DEFAULT_MENU_ITEMS: DropdownMenuItems = [];

export interface BadgeDropdownProps {
  /** Badge icon (left side of badge) */
  icon?: ReactNode;

  /** Main badge text */
  label: ReactNode;

  /** Additional count text (e.g., "+2") */
  count?: number;

  /** Tooltip text shown on hover */
  tooltipTitle?: ReactNode;

  /** Dropdown menu items - if provided, enables dropdown functionality */
  menuItems?: DropdownMenuItems;

  /** Loading state */
  isLoading?: boolean;

  /** Error state */
  error?: unknown;

  /** Alternative content to show in loading state */
  loadingContent?: ReactNode;

  /** Alternative content to show in error state */
  errorContent?: ReactNode;

  /** Optional callback when menu item is clicked */
  onMenuItemClick?: (key: string) => void;

  /** Optional CSS class */
  className?: string;
}

/**
 * BadgeDropdown: A reusable badge component with optional dropdown
 *
 * Can be used as:
 * - Simple badge: Just show label + icon
 * - Badge with dropdown: Click to see details
 *
 * Use cases:
 * - System roles (PLAT-7980)
 * - Permission indicators
 * - Status badges with details
 * - Any label pattern needing optional expansion
 *
 * @example
 * // Simple badge (no dropdown)
 * <BadgeDropdown
 *   icon={<LockOutlined />}
 *   label="Cloud Admin"
 *   count={2}
 * />
 *
 * @example
 * // Badge with dropdown
 * <BadgeDropdown
 *   icon={<LockOutlined />}
 *   label="Cloud Admin"
 *   count={2}
 *   tooltipTitle="Your system roles"
 *   menuItems={[...]}  // Optional - if provided, enables dropdown
 * />
 */

const createMenuItemLabel = (
  label: React.ReactNode,
  description?: string,
  icon?: ReactNode,
  iconColor?: string,
) => (
  <Styled.MenuItem>
    <div className='menu-item-label'>
      {icon && (
        <span style={{ color: iconColor }}>
          {icon}
          {' '}
        </span>
      )}
      {label}
    </div>
    {description && (
      <div className='menu-item-description'>{description}</div>
    )}
  </Styled.MenuItem>
);

const createGroupChildren = (groupItem: DropdownMenuGroup) =>
  groupItem.children.map((child) => ({
    label: createMenuItemLabel(child.label, child.description, child.icon, child.iconColor),
    key: child.key,
  }));

const mapItemToAntDropdownItem = (item: DropdownMenuGroup): NonNullable<MenuProps['items']>[number] => ({
  label: item.label,
  type: 'group' as const,
  children: createGroupChildren(item),
});

const buildMenuItems = (items: DropdownMenuItems): NonNullable<MenuProps['items']> =>
  items.map(mapItemToAntDropdownItem);

interface BadgeContentConfig {
  icon: React.ReactNode | undefined;
  label: ReactNode;
  count: number | undefined;
  className: string | undefined;
  hasDropdown: boolean;
}

const renderBadgeContent = ({ icon, label, count, className, hasDropdown }: BadgeContentConfig): React.ReactElement => (
  <Styled.BadgeContainer
    withborder={hasDropdown}
    size='medium'
    role={hasDropdown ? 'button' : undefined}
    tabIndex={hasDropdown ? 0 : -1}
    aria-label={typeof label === 'string' ? `${label}${count ? ` +${count}` : ''}` : undefined}
    className={className}
  >
    {icon && <Styled.BadgeIcon size='medium'>{icon}</Styled.BadgeIcon>}
    <Styled.BadgeText>
      {label}
      {count && count > 0 && ` +${count}`}
    </Styled.BadgeText>
  </Styled.BadgeContainer>
);

const renderSimpleBadge = (
  badge: React.ReactElement,
  tooltipTitle: React.ReactNode | undefined,
): React.ReactElement => {
  if (tooltipTitle) {
    return <Tooltip title={tooltipTitle}>{badge}</Tooltip>;
  }

  return badge;
};

interface BadgeWithDropdownProps {
  badge: React.ReactElement;
  menuItems: DropdownMenuItems;
  tooltipTitle: React.ReactNode | undefined;
  onMenuItemClick: ((key: string) => void) | undefined;
}

const BadgeWithDropdown = ({
  badge,
  menuItems,
  tooltipTitle,
  onMenuItemClick,
}: BadgeWithDropdownProps): React.ReactElement => {
  const transformedItems = buildMenuItems(menuItems);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  return (
    <Tooltip title={tooltipTitle}>
      <div ref={dropdownRef}>
        <Dropdown
          menu={{
            items: transformedItems,
            onClick: (info) => onMenuItemClick?.(info.key),
          }}
          placement='bottomRight'
          autoAdjustOverflow={false}
          trigger={['click']}
        >
          {badge}
        </Dropdown>
      </div>
    </Tooltip>
  );
};

const BadgeDropdown: React.FC<BadgeDropdownProps> = ({
  icon,
  label,
  count,
  tooltipTitle,
  menuItems = DEFAULT_MENU_ITEMS,
  isLoading = false,
  error,
  loadingContent,
  errorContent,
  onMenuItemClick,
  className,
}): React.ReactElement | null => {
  if (isLoading) {
    return (loadingContent as React.ReactElement) || <Skeleton.Avatar size='small' shape='circle' />;
  }

  if (error) {
    return (errorContent as React.ReactElement) || null;
  }

  const hasDropdown = menuItems && menuItems.length > 0;
  const badge = renderBadgeContent({ icon, label, count, className, hasDropdown });

  if (!hasDropdown) {
    return renderSimpleBadge(badge, tooltipTitle);
  }

  return (
    <BadgeWithDropdown
      badge={badge}
      menuItems={menuItems}
      tooltipTitle={tooltipTitle}
      onMenuItemClick={onMenuItemClick}
    />
  );
};

export default BadgeDropdown;
